
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { getModule } from 'vuex-module-decorators';
import store from '@/store';
import PhotosModule from '@/store/modules/Photos';
import SessionsModule from '@/store/modules/Sessions';
import ElementModel from '@/model/SessionListElement';
import { createInterfaceSound, playInterfaceSound } from '@/service/interfaceSound';

const photosModule = getModule(PhotosModule, store);
const sessionsModule = getModule(SessionsModule, store);

@Component({})
export default class SessionInfoDialog extends Vue {
    @Prop({ required: true })
    private session!: ElementModel;

    public menuOpen: boolean = false;
    private refreshedSession: ElementModel | null = null;

    playSlideSound() {
        const audio = createInterfaceSound('/sound/slide.wav');
        playInterfaceSound(audio).catch(error => {
            console.log('Audio playback failed:', error);
        });
    }

    async refreshSessionData() {
        try {
            // Load fresh session data from the API
            await sessionsModule.loadSession(this.session.id);
            // Get the updated session from the store
            const updatedSession = sessionsModule.sessions[this.session.id];
            if (updatedSession) {
                // Update the local session data with fresh information
                this.refreshedSession = {
                    ...this.session,
                    chatEnabled: updatedSession.chatEnabled,
                    downloadEnabled: updatedSession.downloadEnabled,
                    finalized: updatedSession.finalized,
                    finalizationEnabled: updatedSession.finalizationEnabled,
                    watermark: updatedSession.watermark || this.session.watermark,
                    // Keep other properties from the original session
                };
            }
        } catch (error) {
            console.error('Failed to refresh session data:', error);
            // Keep using the original session data if refresh fails
            this.refreshedSession = null;
        }
    }

    @Watch('menuOpen')
    async onMenuOpenChange(value: boolean) {
        if (value) {
            this.playSlideSound();
            // Refresh session data when dialog opens
            await this.refreshSessionData();
        }
    }

    // Use refreshed session data if available, otherwise fall back to original
    get currentSession() {
        return this.refreshedSession || this.session;
    }

    get sessionPhotosCount() {
        const sessionPhotosList = photosModule.sessionPhotos[this.currentSession.id];
        return sessionPhotosList ? sessionPhotosList.length : 0;
    }

    get sessionShared() {
        // This should be passed as a prop or calculated based on the session data
        return this.currentSession.shared;
    }

    get hasSharedLinks() {
        // Check if the session has any shared links
        // This could be enhanced to check actual link data from API
        return this.currentSession.shared && this.currentSession.ordered;
    }

    get hasWatermark() {
        // Check if there's actually a watermark applied
        // A session might have a watermark object but no actual watermark
        if (!this.currentSession.watermark) {
            return false;
        }
        
        // Check if watermark has actual content (not just default/empty object)
        const watermark = this.currentSession.watermark;
        return watermark && (
            watermark.enabled === true || 
            (watermark.opacity && watermark.opacity > 0) || 
            (watermark.size && watermark.size > 0) ||
            watermark.image
        );
    }

    get sessionOrderCount() {
        // For now, we'll use the ordered property as a simple indicator
        // In a real implementation, you might want to fetch actual order count from API
        return this.currentSession.ordered ? 1 : 0;
    }

    get sessionStorageSize() {
        // TODO: Implement actual session storage size calculation
        // This could be calculated by:
        // 1. Getting file sizes for all photos in the session via API
        // 2. Summing up the sizes
        // 3. Converting to human-readable format (B, kB, MB, GB)
        
        const sessionPhotosList = photosModule.sessionPhotos[this.currentSession.id];
        if (!sessionPhotosList || sessionPhotosList.length === 0) {
            return '0 B';
        }
        
        // Placeholder: estimate ~5MB per photo (this should be replaced with actual calculation)
        const estimatedSizeBytes = sessionPhotosList.length * 5 * 1024 * 1024;
        return this.formatBytes(estimatedSizeBytes);
    }

    private formatBytes(bytes: number): string {
        const units = ['B', 'kB', 'MB', 'GB', 'TB'];
        let value = bytes;
        let unitIndex = 0;

        while (value >= 1024 && unitIndex < units.length - 1) {
            value /= 1024;
            unitIndex++;
        }

        return value % 1 !== 0 
            ? `${value.toFixed(1)} ${units[unitIndex]}`
            : `${Math.floor(value)} ${units[unitIndex]}`;
    }
}
